import { useNavigate } from 'react-router-dom';
import { type AssessmentResult } from '../types/assessment';
import { downloadPDF } from '../services/pdfGenerator';

interface Props {
  result: AssessmentResult;
  onClear: () => void;
}

function safeNum(value: number | null | undefined, fallback: number = 0): number {
  return typeof value === 'number' && !isNaN(value) ? value : fallback;
}

export default function ReportView({ result, onClear }: Props) {
  const navigate = useNavigate();

  const handleDownloadPDF = () => {
    downloadPDF(result);
  };

  const getScoreClass = (score: number) => {
    if (score >= 80) return 'score-high';
    if (score >= 60) return 'score-medium';
    return 'score-low';
  };

  const nonPassingChecks = result.checks.filter(c => c.status !== 'PASS');

  const overallScore = safeNum(result.overallScore);
  const coverage = safeNum(result.coverage);
  const totalChecks = safeNum(result.summary?.totalChecks);
  const passCount = safeNum(result.summary?.passCount);
  const warningCount = safeNum(result.summary?.warningCount);
  const failCount = safeNum(result.summary?.failCount);
  const notAssessedCount = safeNum(result.summary?.notAssessedCount);

  return (
    <div>
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Assessment Report</h2>
          <div className="btn-group" style={{ marginTop: 0 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/results')}>
              Back to Results
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF}>
              Download PDF
            </button>
            <button className="btn btn-danger" onClick={onClear}>
              Clear Session
            </button>
          </div>
        </div>

        {/* Executive Summary */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: 'var(--color-primary)', marginBottom: '1rem', fontSize: '1.125rem' }}>
            Executive Summary
          </h3>
          
          <div className="scoreboard" style={{ marginBottom: '1.5rem' }}>
            <div className="score-card">
              <div className={`score-value ${getScoreClass(overallScore)}`}>
                {overallScore}/100
              </div>
              <div className="score-label">Overall Score</div>
            </div>
            <div className="score-card">
              <div className="score-value">{coverage.toFixed(0)}%</div>
              <div className="score-label">Coverage</div>
            </div>
            <div className="score-card">
              <div className="score-value score-low">{failCount}</div>
              <div className="score-label">Critical Findings</div>
            </div>
            <div className="score-card">
              <div className="score-value score-medium">{warningCount}</div>
              <div className="score-label">High Findings</div>
            </div>
          </div>

          <p style={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>
            This CrowdStrike Falcon Health Check Assessment was conducted for <strong>{result.customerName || 'N/A'}</strong> by <strong>{result.assessorName || 'N/A'}</strong> on {' '}
            <strong>{result.assessmentDate ? new Date(result.assessmentDate).toLocaleDateString() : 'N/A'}</strong> covering the previous <strong>{result.assessmentPeriodDays ?? 'N/A'} days</strong>.
          </p>
          
          <p style={{ fontSize: '0.9375rem', lineHeight: 1.6, marginTop: '0.75rem' }}>
            Out of {totalChecks} checks evaluated, {passCount} passed, {warningCount} returned warnings, 
            and {failCount} failed. {notAssessedCount} checks could not be assessed due to data availability or API limitations.
          </p>

          {failCount > 0 && (
            <div style={{ 
              background: '#fce8e8', 
              border: '1px solid #f5c6cb', 
              borderRadius: 'var(--radius)', 
              padding: '1rem', 
              marginTop: '1rem',
              fontSize: '0.9375rem' 
            }}>
              <strong>Action Required:</strong> {failCount} critical finding(s) require immediate attention. 
              Please review the detailed findings below and prioritize remediation efforts.
            </div>
          )}
        </div>

        {/* Methodology */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: 'var(--color-primary)', marginBottom: '1rem', fontSize: '1.125rem' }}>
            Assessment Methodology
          </h3>
          <p style={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>
            This assessment uses the CrowdStrike Falcon REST API to collect read-only data about the endpoint security posture. 
            The evaluation covers five key areas: Endpoint and Sensor Health, Prevention Policy Assessment, Detection and Alert Posture, 
            Host Group Assessment, and Exclusions Assessment. Each check is evaluated against established best practices and produces 
            a status of PASS, WARNING, FAIL, or NOT ASSESSED.
          </p>
          <p style={{ fontSize: '0.9375rem', lineHeight: 1.6, marginTop: '0.75rem' }}>
            <strong>Scoring:</strong> PASS = 100 points, WARNING = 50 points, FAIL = 0 points. NOT ASSESSED checks are excluded from scoring. 
            The overall score is the average of all category scores.
          </p>
        </div>

        {/* Category Results */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: 'var(--color-primary)', marginBottom: '1rem', fontSize: '1.125rem' }}>
            Category Results
          </h3>
          
          {result.categoryScores.map(cat => {
            const catScore = safeNum(cat.score);
            const catPass = safeNum(cat.passCount);
            const catWarn = safeNum(cat.warningCount);
            const catFail = safeNum(cat.failCount);
            const catNA = safeNum(cat.notAssessedCount);
            const catTotal = safeNum(cat.totalChecks);
            return (
              <div key={cat.category} style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: 600 }}>{cat.category}</h4>
                  <span className={`status ${getScoreClass(catScore)}`} style={{ fontSize: '0.875rem' }}>
                    {catScore}/100
                  </span>
                </div>
                <div className="progress-bar" style={{ marginBottom: '0.5rem' }}>
                  <div 
                    className="progress-bar-fill" 
                    style={{ 
                      width: `${catScore}%`, 
                      background: catScore >= 80 ? 'var(--color-success)' : catScore >= 60 ? 'var(--color-warning)' : 'var(--color-danger)' 
                    }} 
                  />
                </div>
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                  {catPass} passed, {catWarn} warnings, {catFail} failed, {catNA} not assessed (of {catTotal} total)
                </p>
              </div>
            );
          })}
        </div>

        {/* Detailed Findings */}
        {nonPassingChecks.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: 'var(--color-primary)', marginBottom: '1rem', fontSize: '1.125rem' }}>
              Detailed Findings & Recommendations
            </h3>
            
            {nonPassingChecks.map(check => (
              <div key={check.checkId} style={{ 
                background: check.status === 'FAIL' ? '#fce8e8' : check.status === 'WARNING' ? '#fff8e1' : '#f1f3f4',
                borderLeft: `4px solid ${check.status === 'FAIL' ? 'var(--color-danger)' : check.status === 'WARNING' ? 'var(--color-warning)' : 'var(--color-text-muted)'}`,
                padding: '1rem 1.25rem',
                borderRadius: 'var(--radius)',
                marginBottom: '0.75rem'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                  {check.checkId}: {check.checkName}
                  <span style={{ 
                    marginLeft: '0.75rem', 
                    fontSize: '0.75rem', 
                    padding: '0.125rem 0.5rem', 
                    borderRadius: 'var(--radius)',
                    background: check.status === 'FAIL' ? '#f5c6cb' : check.status === 'WARNING' ? '#ffeeba' : '#d6d8db',
                    color: check.status === 'FAIL' ? '#721c24' : check.status === 'WARNING' ? '#856404' : '#383d41'
                  }}>
                    {check.status}
                  </span>
                </div>
                <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}><strong>Category:</strong> {check.category}</p>
                <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}><strong>Finding:</strong> {check.finding}</p>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}><strong>Recommendation:</strong> {check.recommendation}</p>
              </div>
            ))}
          </div>
        )}

        {/* Limitations */}
        {result.limitations && result.limitations.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: 'var(--color-primary)', marginBottom: '1rem', fontSize: '1.125rem' }}>
              Assessment Limitations
            </h3>
            <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9375rem' }}>
              {result.limitations.map((limitation, idx) => (
                <li key={idx} style={{ marginBottom: '0.5rem' }}>{limitation}</li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ 
          background: '#e6f4ea', 
          border: '1px solid #34a853', 
          borderRadius: 'var(--radius)', 
          padding: '1rem',
          fontSize: '0.875rem',
          color: '#1e8e3e'
        }}>
          <strong>Data Privacy:</strong> This assessment was conducted in a completely stateless manner. 
          No customer data, credentials, or results were stored by this application. The report shown here 
          exists only in your browser memory. Download the PDF for your records.
        </div>
      </div>
    </div>
  );
}
